/** Plot colors are hex, not theme tokens: ECharts paints to canvas and can't read a CSS variable. */

export const PLOT_X_AXIS_ID = "plot-time";

/** The line under the plot. */
export const PLOT_BASELINE_COLOR = "#E7EAE8";

/** Gridlines, the same color as the baseline. */
export const GRID_LINE_COLOR = "#E7EAE8";

/** Axis tick labels. 4.9:1 on the card: this is the floor, don't lighten it. */
export const AXIS_LABEL_COLOR = "#6B7671";

/** Series stroke. Wider than the spec's 1.75 — 2 reads better against real data. */
export const SERIES_STROKE_WIDTH_PX = 2;

/** Crosshair following the pointer, dashed 3 3. */
export const CROSSHAIR_HOVER_COLOR = "#9AA5A0";

/** Distance of a y-axis line from the plot grid edge — ECharts offset and gutter measurement both use this. */
export const Y_AXIS_BASE_OFFSET_PX = 10;

/** Matches --font-sans. Canvas Roboto was backed out once for rendering badly (#1837). */
export const CHART_FONT_FAMILY = "Roboto, sans-serif";

/** Y-axis tick label font size — must match Y_TICK_FONT. */
export const Y_TICK_FONT_SIZE_PX = 11;

/**
 * Must match the rendered style of the ECharts y-axis tick labels (axisLabel in
 * buildTimePanelOptions) — measured widths depend on it.
 */
export const Y_TICK_FONT = `400 ${Y_TICK_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;

/**
 * Per-series value tags beside the crosshair dots (hover-values.tsx).
 */
export const HOVER_LABEL_FONT_SIZE_PX = 10;
export const HOVER_LABEL_RADIUS_PX = 4;
export const HOVER_LABEL_PADDING_X_PX = 6;
export const HOVER_LABEL_BORDER_PX = 1;

/** Value tag and tooltip value text. Never the series colour — the fill and border carry identity. */
export const HOVER_LABEL_TEXT_COLOR = "#111815";

/** Tooltip row labels — a step darker than the axis ticks, a step lighter than the values. */
export const TOOLTIP_LABEL_COLOR = "#4A544F";

/** Tooltip box: white on the card, hairline border, and one row every 15px. */
export const TOOLTIP_BACKGROUND = "#FFFFFF";
export const TOOLTIP_RADIUS_PX = 8;
export const TOOLTIP_ROW_HEIGHT_PX = 15;
export const TOOLTIP_FONT_SIZE_PX = 11;
export const HOVER_LABEL_FONT_WEIGHT = 600;
export const HOVER_LABEL_FONT = `${HOVER_LABEL_FONT_WEIGHT} ${HOVER_LABEL_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;

/** Gap between the baseline and the x-axis tick labels. */
export const X_AXIS_TICK_LABEL_MARGIN_PX = 8;

export const X_AXIS_TICK_LABEL_FONT_SIZE_PX = 11;

/** Includes enough space below the baseline for letters such as "g" in month names. */
export const X_AXIS_TICK_LABEL_LINE_HEIGHT_PX = 12;

/** The hovered time, drawn by echarts' own axisPointer label below the baseline. */
export const CROSSHAIR_LABEL_BACKGROUND = "#1C2320";
export const CROSSHAIR_LABEL_TEXT_COLOR = "#FFFFFF";
export const CROSSHAIR_LABEL_FONT_SIZE_PX = 10;
export const CROSSHAIR_LABEL_FONT_WEIGHT = 500;
export const CROSSHAIR_LABEL_FONT = `${CROSSHAIR_LABEL_FONT_WEIGHT} ${CROSSHAIR_LABEL_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;
/**
 * Set explicitly, not left to echarts: a measured line height for 10px text is fractional, which
 * lands the pill's top edge between pixels and paints it grey on a 1x screen.
 */
export const CROSSHAIR_LABEL_LINE_HEIGHT_PX = 12;
export const CROSSHAIR_LABEL_RADIUS_PX = 6;
export const CROSSHAIR_LABEL_PADDING_X_PX = 6;
/** Separate top and bottom: the digits sit optically low in the box, so the top carries one more. */
export const CROSSHAIR_LABEL_PADDING_TOP_PX = 2;
export const CROSSHAIR_LABEL_PADDING_BOTTOM_PX = 1;
export const CROSSHAIR_LABEL_BORDER_PX = 0;

/** What echarts paints for that font and padding — the band has to cover it or it clips. */
export const CROSSHAIR_LABEL_HEIGHT_PX =
    CROSSHAIR_LABEL_LINE_HEIGHT_PX +
    CROSSHAIR_LABEL_PADDING_TOP_PX +
    CROSSHAIR_LABEL_PADDING_BOTTOM_PX +
    2 * CROSSHAIR_LABEL_BORDER_PX;

/** Gap from the baseline to the crosshair's time pill. */
export const X_AXIS_POINTER_LABEL_MARGIN_PX = 6;

/**
 * Space below the plot. The time pill is meant to cover the tick labels rather than sit clear of
 * them, so this is the taller of the two, not their sum.
 */
export const X_AXIS_BAND_PX = Math.max(
    X_AXIS_TICK_LABEL_MARGIN_PX + X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
    X_AXIS_POINTER_LABEL_MARGIN_PX + CROSSHAIR_LABEL_HEIGHT_PX,
);

/** Space between the highest labelled tick and the top of the plot. */
export const PLOT_HEADROOM_PX = 0;

/** Half the top tick label hangs above its gridline, so the plot starts this far down the canvas. */
export const TOP_TICK_LABEL_OVERHANG_PX = Math.ceil(Y_TICK_FONT_SIZE_PX / 2);

/** Vertical gap between the card's header row, plot and legend. */
export const CARD_GAP_PX = 12;
